import { DatePipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IPromotion, PromotionStatus } from 'core';
import { KorviuButtonComponent } from 'shared';
import { TagComponent, TagComponentType } from 'shared/components/tag/tag.component';

@Component({
  selector: 'app-promotion-card',
  imports: [DatePipe, KorviuButtonComponent, TagComponent, TranslatePipe],
  templateUrl: './promotion-card.component.html',
  styleUrl: './promotion-card.component.scss'
})
export class PromotionCardComponent {
  readonly #router = inject(Router);
  readonly #translateService = inject(TranslateService);

  readonly promotion = input.required<IPromotion>();
  readonly variant = input<PromotionStatus>('featured');
  readonly isCardLink = computed(() => this.variant() !== 'featured');

  readonly availableEntriesLabel = computed(() => {
    if (this.promotion().entries_unlimited) {
      return this.#translateService.instant('promotions.missions.unlimited');
    }

    return this.#getCountLabel(
      'promotions.availableEntries',
      'promotions.availableEntries_other',
      this.promotion().available_entries
    );
  });
  readonly appliedEntriesLabel = computed(() => {
    const appliedEntries = this.promotion().applied_entries;

    if (appliedEntries == null) {
      return '';
    }

    return this.#getCountLabel('promotions.appliedEntries', 'promotions.appliedEntries_other', appliedEntries);
  });
  readonly ticketsLabel = computed(() => {
    return this.#getCountLabel('promotions.tickets', 'promotions.tickets_other', this.promotion().tickets);
  });
  readonly timeRemainingLabel = computed(() => {
    const endDate = this.promotion().end_date;

    if (!endDate) {
      return '';
    }

    return this.#getTimeRemainingLabel(endDate);
  });
  readonly resultLabelKey = computed(() => {
    const result = this.promotion().result;

    return result ? `promotions.past.results.${result}` : 'promotions.past.results.not_participated';
  });
  readonly resultTagType = computed<TagComponentType>(() => {
    switch (this.promotion().result) {
      case 'won':
        return 'success';
      case 'lost':
        return 'error';
      case 'pending_draw':
        return 'warning';
      default:
        return 'info';
    }
  });
  readonly statusLabelKey = computed(() => {
    const status = this.promotion().status;

    return status ? `promotions.campaignDetails.statuses.${status}` : '';
  });
  readonly topRewardLabel = computed(() => {
    const reward = this.promotion().top_reward;

    if (!reward) {
      return '';
    }

    const typeLabel = this.#translateService.instant(`promotions.campaignDetails.rewardTypes.${reward.reward_type}`);

    return [reward.value, typeLabel].filter(Boolean).join(' ');
  });
  readonly objectiveLabel = computed(() => {
    const objective = this.promotion().objective;

    if (!objective?.action) {
      return '';
    }

    const action = this.#translateService.instant(`promotions.campaignDetails.objectives.${objective.action}`);

    return this.#translateService.instant('promotions.campaignDetails.objectiveSummary', {
      action,
      count: objective.min_count
    });
  });
  readonly claimStatusLabelKey = computed(() => {
    const claimStatus = this.promotion().claim_status;

    return claimStatus ? `promotions.campaignDetails.claimStatuses.${claimStatus}` : '';
  });

  public openPromotionDetails(): void {
    if (!this.isCardLink()) {
      return;
    }

    const promotion = this.promotion();
    const commands =
      promotion.source === 'campaign'
        ? ['/workspace/promotions/campaign', promotion.id]
        : ['/workspace/promotions', promotion.id];

    void this.#router.navigate(commands);
  }

  public onCardKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.openPromotionDetails();
  }

  #getTimeRemainingLabel(endDate: string): string {
    const endTimestamp = new Date(endDate).getTime();

    if (Number.isNaN(endTimestamp)) {
      return '';
    }

    const diffInMs = endTimestamp - Date.now();
    const hourInMs = 1000 * 60 * 60;
    const dayInMs = hourInMs * 24;

    if (diffInMs <= 0) {
      return this.#translateService.instant('promotions.expired');
    }

    const totalDays = Math.floor(diffInMs / dayInMs);
    if (totalDays >= 1) {
      return this.#getCountLabel('promotions.daysLeft', 'promotions.daysLeft_other', totalDays);
    }

    const totalHours = Math.floor(diffInMs / hourInMs);
    if (totalHours >= 1) {
      return this.#getCountLabel('promotions.hoursLeft', 'promotions.hoursLeft_other', totalHours);
    }

    return this.#translateService.instant('promotions.lessThanHourLeft');
  }

  #getCountLabel(singularKey: string, pluralKey: string, count: number): string {
    return this.#translateService.instant(count === 1 ? singularKey : pluralKey, { count });
  }
}
